import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { sanitizeHtml } from '../lib/sanitizeHtml';
import { setFavicon } from '../lib/favicon';

export default function SubgroupPage() {
  const { slug, pageKey } = useParams();
  const { getSubgroupPage, org } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(''); // '' | 'notfound' | 'denied'

  const load = useCallback(() => {
    setLoading(true); setStatus('');
    getSubgroupPage(slug, pageKey)
      .then(setData)
      .catch((e) => setStatus(e.response && e.response.status === 403 ? 'denied' : 'notfound'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, pageKey]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data && data.subgroup && data.subgroup.favicon_url) setFavicon(data.subgroup.favicon_url);
    return () => setFavicon(org && org.favicon_url);
  }, [data, org]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-16 flex items-center gap-2" style={{ color: 'var(--olive)' }}><Loader2 className="animate-spin" size={16} /> Loading…</div>;

  if (status === 'denied') return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="subgroup-page-denied">
      <div className="mx-auto mb-5 flex items-center justify-center w-16 h-16 rounded-full" style={{ background: 'var(--maroon)' }}><Lock size={28} color="var(--cream)" /></div>
      <h2 className="font-title text-2xl mb-2" style={{ color: 'var(--ink)' }}>Members only</h2>
      <p className="text-sm mb-4" style={{ color: 'var(--olive)' }}>This page is available to members of this group only.</p>
      <Link to={`/g/${slug}`} className="text-sm underline">Back to group</Link>
    </div>
  );

  if (status === 'notfound' || !data) return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="subgroup-page-notfound">
      <h1 className="font-title text-2xl" style={{ color: 'var(--maroon)' }}>Page not found</h1>
      <Link to={`/g/${slug}`} className="text-sm underline">Back to group</Link>
    </div>
  );

  const { subgroup, page } = data;
  return (
    <div className="max-w-3xl mx-auto px-4 py-10 fade-in" data-testid="subgroup-page">
      <Link to={`/g/${slug}`} className="text-sm underline mb-3 inline-block" style={{ color: 'var(--olive)' }}>← {subgroup.name}</Link>
      <h1 className="font-title text-3xl md:text-4xl mb-2" style={{ color: 'var(--maroon)' }}>{page.title}</h1>
      <div className="h-1 w-20 mb-6" style={{ background: 'var(--gold)' }} />
      {page.intro && <p className="text-[15px] mb-4" style={{ color: 'var(--ink)' }}>{page.intro}</p>}
      {page.body && <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.body) }} />}
      {page.html && <div className="prose prose-sm max-w-none mt-4" dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.html) }} />}
    </div>
  );
}
